import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./RouteManagement.module.css";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { Floor, RouteEntity, RoutePoint } from "../../types";

export default function RouteManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: routes } = useQuery({
    queryKey: ["routes"],
    queryFn: () => apiService.getRoutes(),
  });

  const { data: shops } = useQuery({
    queryKey: ["shops"],
    queryFn: () => apiService.getShops(),
  });

  const { data: terminals } = useQuery({
    queryKey: ["terminals"],
    queryFn: () => apiService.getTerminals(),
  });

  const { data: floors } = useQuery({
    queryKey: ["floors"],
    queryFn: () => apiService.getFloors(),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<RouteEntity | null>(null);
  const [selectedShopId, setSelectedShopId] = useState<number | "">("");
  const [selectedTerminalId, setSelectedTerminalId] = useState<number | "">("");
  const [shopSearchQuery, setShopSearchQuery] = useState<string>("");
  const [isShopDropdownOpen, setIsShopDropdownOpen] = useState<boolean>(false);
  const [filterShopName, setFilterShopName] = useState<string>("");
  type EditorPoint = RoutePoint & { floor: number };
  const [editorPoints, setEditorPoints] = useState<EditorPoint[]>([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [floorMap, setFloorMap] = useState<HTMLImageElement | null>(null);
  const [terminalIcon, setTerminalIcon] = useState<HTMLImageElement | null>(
    null,
  );
  const [shopActiveIcon, setShopActiveIcon] = useState<HTMLImageElement | null>(
    null,
  );
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);

  const activeFloor = useMemo<Floor | undefined>(() => {
    if (!floors) return undefined;
    // Prefer manually selected floor
    if (selectedFloor) return selectedFloor;
    // Prefer terminal floor if terminal selected
    if (selectedTerminalId && terminals?.length) {
      const term = terminals.find((t) => Number(t.id) === selectedTerminalId);
      if (term) return floors.find((f) => f.number === term.floor);
    }
    // Fallback to shop floor if shop selected
    if (selectedShopId && shops?.length) {
      const shop = shops.find((s) => Number(s.id) === selectedShopId);
      if (shop) return floors.find((f) => f.number === shop.floor);
    }
    return undefined;
  }, [
    floors,
    shops,
    terminals,
    selectedShopId,
    selectedTerminalId,
    selectedFloor,
  ]);

  const mediaBase = process.env.REACT_APP_MEDIA_PATH;
  const floorMapUrl = useMemo(() => {
    const url = activeFloor?.mapUrl || "";
    if (!url) return "";
    return url.startsWith("http")
      ? url
      : `${mediaBase}${url.startsWith("/") ? "" : "/"}${url}`;
  }, [activeFloor, mediaBase]);

  // Load floor map image for canvas
  useEffect(() => {
    if (!floorMapUrl) {
      setFloorMap(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setFloorMap(img);
    img.onerror = () => console.error("Failed to load floor map for routes");
    img.src = floorMapUrl;
  }, [floorMapUrl]);

  // Load terminal icon image
  useEffect(() => {
    if (!selectedTerminalId || !terminals?.length) {
      setTerminalIcon(null);
      return;
    }
    const t = terminals.find((tt) => Number(tt.id) === selectedTerminalId);
    if (!t) {
      setTerminalIcon(null);
      return;
    }
    const iconUrlRaw =
      t.mapIconActiveUrl ||
      t.mapIconInactiveUrl ||
      "/uploads/terminal/terminal.svg";
    const iconUrl = iconUrlRaw.startsWith("http")
      ? iconUrlRaw
      : `${mediaBase}${iconUrlRaw.startsWith("/") ? "" : "/"}${iconUrlRaw}`;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setTerminalIcon(img);
    img.onerror = () =>
      console.error("Failed to load terminal icon for routes");
    img.src = iconUrl;
  }, [selectedTerminalId, terminals, mediaBase]);

  // Load selected shop ACTIVE icon
  useEffect(() => {
    if (!selectedShopId || !shops?.length) {
      setShopActiveIcon(null);
      return;
    }
    const s = shops.find((ss) => Number(ss.id) === selectedShopId);
    if (!s || !s.mapIconActiveUrl) {
      setShopActiveIcon(null);
      return;
    }
    const iconUrl = s.mapIconActiveUrl.startsWith("http")
      ? s.mapIconActiveUrl
      : `${mediaBase}${s.mapIconActiveUrl.startsWith("/") ? "" : "/"}${
          s.mapIconActiveUrl
        }`;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setShopActiveIcon(img);
    img.onerror = () => console.error("Failed to load selected shop icon");
    img.src = iconUrl;
  }, [selectedShopId, shops, mediaBase]);

  // Derived flattened points for payload/validation
  const points: RoutePoint[] = useMemo(() => {
    return editorPoints.map((p) => ({ x: p.x, y: p.y, floor: p.floor }));
  }, [editorPoints]);

  // Canvas draw
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw floor map with fit + pan + zoom (like MallMap)
    let scaleToFit = 1;
    if (floorMap) {
      scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9,
      );
      const scaledWidth = floorMap.width * scaleToFit * scale;
      const scaledHeight = floorMap.height * scaleToFit * scale;
      const mapX = (canvas.width - scaledWidth) / 2 + pan.x;
      const mapY = (canvas.height - scaledHeight) / 2 + pan.y;
      ctx.drawImage(floorMap, mapX, mapY, scaledWidth, scaledHeight);
    }

    // Prepare transformed space
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.translate(pan.x, pan.y);
    ctx.scale(scale * scaleToFit, scale * scaleToFit);

    // Draw terminal icon at its position ONLY if it's on the active floor
    if (selectedTerminalId && terminals?.length) {
      const t = terminals.find((tt) => Number(tt.id) === selectedTerminalId);
      if (t && (!activeFloor || t.floor === activeFloor.number)) {
        const tx = Number(t.positionX) || 0;
        const ty = Number(t.positionY) || 0;
        const scaleX = Number(t.scaleX) || 1;
        const scaleY = Number(t.scaleY) || 1;
        if (terminalIcon) {
          const iconW = terminalIcon.width * scaleX;
          const iconH = terminalIcon.height * scaleY;
          ctx.drawImage(
            terminalIcon,
            tx * 100 - iconW / 2,
            ty * 100 - iconH / 2,
            iconW,
            iconH,
          );
        } else {
          // Fallback marker
          ctx.fillStyle = "rgba(0,0,0,0.6)";
          ctx.beginPath();
          ctx.arc(tx * 100, ty * 100, 14, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // Draw SELECTED shop active icon ONLY if it's on the active floor
    if (selectedShopId && shops?.length) {
      const s = shops.find((ss) => Number(ss.id) === selectedShopId);
      if (s && (!activeFloor || s.floor === activeFloor.number)) {
        const sx = Number(s.positionX) || 0;
        const sy = Number(s.positionY) || 0;
        const scaleX = Number(s.scaleX) || 1;
        const scaleY = Number(s.scaleY) || 1;
        if (shopActiveIcon) {
          const iconW = shopActiveIcon.width * scaleX;
          const iconH = shopActiveIcon.height * scaleY;
          ctx.drawImage(
            shopActiveIcon,
            sx * 100 - iconW / 2,
            sy * 100 - iconH / 2,
            iconW,
            iconH,
          );
        } else {
          // Fallback marker for shop
          ctx.fillStyle = "rgba(234, 126, 102, 0.9)";
          ctx.beginPath();
          ctx.arc(sx * 100, sy * 100, 12, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // Draw route polylines per-floor (only the active floor segment)
    if (editorPoints.length > 0 && activeFloor) {
      const floorPoints = editorPoints.filter(
        (p) => p.floor === activeFloor.number,
      );
      if (floorPoints.length > 0) {
        ctx.strokeStyle = "#1e90ff";
        ctx.lineWidth = 3 / (scale * scaleToFit);
        ctx.beginPath();
        floorPoints.forEach((p, idx) => {
          const px = p.x;
          const py = p.y;
          if (idx === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.stroke();

        // points dots
        floorPoints.forEach((p) => {
          ctx.fillStyle = "#ff4757";
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4 / (scale * scaleToFit), 0, Math.PI * 2);
          ctx.fill();
        });
      }
    }

    ctx.restore();
  }, [
    floorMap,
    scale,
    pan,
    selectedTerminalId,
    terminals,
    terminalIcon,
    selectedShopId,
    shops,
    shopActiveIcon,
    editorPoints,
    activeFloor,
  ]);

  // Resize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleResize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      draw();
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [draw]);

  // Redraw on deps change
  useEffect(() => {
    draw();
  }, [draw]);

  // Mouse interactions like MallMap
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsPanning(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
    hasMovedRef.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isPanning) return;
    const dx = e.clientX - lastMousePos.x;
    const dy = e.clientY - lastMousePos.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasMovedRef.current = true;
    setPan((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas && floorMap && !hasMovedRef.current) {
      if (!activeFloor) {
        setIsPanning(false);
        hasMovedRef.current = false;
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9,
      );
      const totalScale = scale * scaleToFit;
      const imageX = (mouseX - canvas.width / 2 - pan.x) / totalScale;
      const imageY = (mouseY - canvas.height / 2 - pan.y) / totalScale;
      setEditorPoints((prev) => [
        ...prev,
        {
          x: Math.round(imageX),
          y: Math.round(imageY),
          floor: activeFloor.number,
        },
      ]);
    }
    setIsPanning(false);
    hasMovedRef.current = false;
  };

  const handleMouseLeave = () => {
    setIsPanning(false);
    hasMovedRef.current = false;
  };

  // Handle wheel for zooming with addEventListener to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    const handleWheelCapture = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setScale((prev) => {
        const newScale = Math.max(0.5, Math.min(5, prev * delta));
        const scaleToFit = Math.min(
          canvas.width / floorMap.width,
          canvas.height / floorMap.height,
          0.9,
        );
        const imageX =
          (mouseX - canvas.width / 2 - pan.x) / (scaleToFit * prev);
        const imageY =
          (mouseY - canvas.height / 2 - pan.y) / (scaleToFit * prev);
        setPan((prevPan) => ({
          x: prevPan.x - imageX * scaleToFit * (newScale - prev),
          y: prevPan.y - imageY * scaleToFit * (newScale - prev),
        }));
        return newScale;
      });
    };

    canvas.addEventListener("wheel", handleWheelCapture, {
      passive: false,
      capture: true,
    });

    return () => {
      canvas.removeEventListener("wheel", handleWheelCapture, {
        capture: true,
      });
    };
  }, [floorMap, pan]);

  const resetForm = () => {
    setEditingRoute(null);
    setSelectedShopId("");
    setSelectedTerminalId("");
    setEditorPoints([]);
    setSelectedFloor(null);
    setShopSearchQuery("");
    setIsShopDropdownOpen(false);
  };

  const handleFloorClick = (floor: Floor) => {
    setSelectedFloor(floor);
    setPan({ x: 0, y: 0 });
    setScale(1);
  };

  const createMutation = useMutation({
    mutationFn: (data: any) => apiService.createRoute(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routes"] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiService.updateRoute(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routes"] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteRoute(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routes"] });
    },
  });

  const handleAdd = () => {
    resetForm();
    setIsModalOpen(true);
  };

  // Update shop search query when selectedShopId changes
  useEffect(() => {
    if (selectedShopId && shops?.length) {
      const shop = shops.find((s) => Number(s.id) === selectedShopId);
      if (shop) {
        setShopSearchQuery(shop.nameRu);
      }
    } else if (!selectedShopId && !isModalOpen) {
      setShopSearchQuery("");
    }
  }, [selectedShopId, shops, isModalOpen]);

  const handleEdit = (route: RouteEntity) => {
    setEditingRoute(route);
    setSelectedShopId(route.shopId);
    setSelectedTerminalId(route.terminalId);
    // Preserve saved floor per point; fallback to terminal/shop floor if missing
    const term = terminals?.find((t) => Number(t.id) === route.terminalId);
    const shop = shops?.find((s) => Number(s.id) === route.shopId);
    const fallbackFloor = term
      ? Number(term.floor)
      : activeFloor?.number || shop?.floor || 0;
    setEditorPoints(
      (route.points || []).map((p: any) => ({
        x: p.x,
        y: p.y,
        floor: typeof p.floor === "number" ? p.floor : fallbackFloor,
      })),
    );
    setSelectedFloor(null);
    setShopSearchQuery(shop?.nameRu || "");
    setIsShopDropdownOpen(false);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Удалить маршрут?")) {
      deleteMutation.mutate(id);
    }
  };

  // Toolbar actions remain the same

  const undoLastPoint = () => setEditorPoints((prev) => prev.slice(0, -1));
  const clearPoints = () => setEditorPoints([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShopId || !selectedTerminalId || points.length < 2) return;
    const payload = {
      shopId: selectedShopId,
      terminalId: selectedTerminalId,
      points,
    };
    if (editingRoute) {
      updateMutation.mutate({ id: String(editingRoute.id), data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  useEffect(() => {
    if (isModalOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isModalOpen]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление маршрутами</div>
        <button
          className={styles.backButton}
          onClick={() => navigate("/admin")}
        >
          Назад
        </button>
      </header>

      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>Маршруты</h1>

        <div className={styles.controlsRow}>
          <button className={styles.addButton} onClick={handleAdd}>
            + Добавить маршрут
          </button>
          <div className={styles.filterContainer}>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="Поиск по наименованию объекта..."
              value={filterShopName}
              onChange={(e) => setFilterShopName(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.list}>
          {(routes || [])
            .filter((r) => {
              if (!filterShopName.trim()) return true;
              const shop = shops?.find((s) => Number(s.id) === r.shopId);
              if (!shop) return false;
              return shop.nameRu
                .toLowerCase()
                .includes(filterShopName.toLowerCase().trim());
            })
            .map((r) => {
              const shop = shops?.find((s) => Number(s.id) === r.shopId);
              const term = terminals?.find(
                (t) => Number(t.id) === r.terminalId,
              );
              return (
                <div className={styles.row} key={r.id}>
                  <div className={styles.rowMain}>
                    <div className={styles.rowTitle}>
                      {/* {shop?.nameRu || `Объект #${r.shopId}`} →{" "}
                    {term?.name || `Терминал #${r.terminalId}`} */}
                      {term?.name || `Терминал #${r.terminalId}`} →{" "}
                      {shop?.nameRu || `Объект #${r.shopId}`}
                    </div>
                    <div className={styles.rowSub}>
                      {r.points?.length || 0} точек
                    </div>
                  </div>
                  <div className={styles.rowActions}>
                    <button
                      className={styles.actionButton}
                      onClick={() => handleEdit(r)}
                    >
                      Редактировать
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.actionDanger}`}
                      onClick={() => handleDelete(String(r.id))}
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              );
            })}
        </div>

        <div
          className={`${styles.modal} ${isModalOpen ? styles.modalOpen : ""}`}
        >
          <div
            ref={modalContentRef}
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modalTitle}>
              {editingRoute ? "Редактировать маршрут" : "Создать маршрут"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Терминал</label>
                <select
                  className={styles.select}
                  value={selectedTerminalId}
                  onChange={(e) =>
                    setSelectedTerminalId(
                      e.target.value ? parseInt(e.target.value, 10) : "",
                    )
                  }
                >
                  <option value="">Выберите терминал</option>
                  {(terminals || []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} (этаж {t.floor})
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Объект</label>
                <div className={styles.searchableSelect}>
                  <input
                    type="text"
                    className={styles.searchableSelectInput}
                    placeholder="Поиск объекта..."
                    value={shopSearchQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setShopSearchQuery(value);
                      setIsShopDropdownOpen(true);
                      // Clear selection if input is cleared
                      if (!value) {
                        setSelectedShopId("");
                      }
                    }}
                    onFocus={() => setIsShopDropdownOpen(true)}
                    onBlur={() => {
                      // Delay to allow click on dropdown item
                      setTimeout(() => setIsShopDropdownOpen(false), 200);
                    }}
                  />
                  {isShopDropdownOpen && (
                    <div className={styles.searchableSelectDropdown}>
                      {(shops || [])
                        .filter((s) =>
                          s.nameRu
                            .toLowerCase()
                            .includes(shopSearchQuery.toLowerCase()),
                        )
                        .map((s) => (
                          <div
                            key={s.id}
                            className={`${styles.searchableSelectOption} ${
                              selectedShopId === Number(s.id)
                                ? styles.searchableSelectOptionSelected
                                : ""
                            }`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSelectedShopId(Number(s.id));
                              setShopSearchQuery(s.nameRu);
                              setIsShopDropdownOpen(false);
                            }}
                          >
                            {s.nameRu}
                          </div>
                        ))}
                      {(shops || []).filter((s) =>
                        s.nameRu
                          .toLowerCase()
                          .includes(shopSearchQuery.toLowerCase()),
                      ).length === 0 && (
                        <div className={styles.searchableSelectNoResults}>
                          Объекты не найдены
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.boardWrapper}>
                <div className={styles.boardToolbar}>
                  <div>Построение по карте: кликайте, чтобы добавить точки</div>
                  <div className={styles.toolbarActions}>
                    <button
                      type="button"
                      className={`${styles.actionButton} ${styles.actionWarning}`}
                      onClick={undoLastPoint}
                      disabled={points.length === 0}
                    >
                      Отменить точку
                    </button>
                    <button
                      type="button"
                      className={`${styles.actionButton} ${styles.actionWarning}`}
                      onClick={clearPoints}
                      disabled={points.length === 0}
                    >
                      Очистить
                    </button>
                  </div>
                </div>
                {floors && floors.length > 0 && (
                  <div className={styles.floorSelector}>
                    {floors.map((floor) => (
                      <button
                        key={floor.id}
                        type="button"
                        className={`${styles.floorButton} ${
                          activeFloor?.id === floor.id
                            ? styles.floorButtonActive
                            : ""
                        }`}
                        onClick={() => handleFloorClick(floor)}
                      >
                        {floor.number}
                      </button>
                    ))}
                  </div>
                )}
                <div className={styles.board}>
                  <canvas
                    ref={canvasRef}
                    className={styles.mapImage}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                  />
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.actionWarning}`}
                  onClick={() => setIsModalOpen(false)}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className={styles.actionButton}
                  disabled={
                    !selectedShopId || !selectedTerminalId || points.length < 2
                  }
                >
                  {editingRoute ? "Сохранить" : "Создать"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
